import { useEffect, useRef, useState } from "react";
import { Newspaper, Sparkles, Loader2, Trash2, Eye, EyeOff, Save, X, ExternalLink, Settings2, PenLine, Layers, Image as ImageIcon, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, resolveImg } from "../../lib/api";
import RichTextEditor from "../../components/RichTextEditor";

const NEW_DRAFT = { isNew: true, title: "", content_html: "", meta_description: "", excerpt: "", keywords: "", cover_image: "", is_pillar: false, published: false };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState({ auto: true, cadence_days: 7, auto_publish: true });
  const [topic, setTopic] = useState("");
  const [publish, setPublish] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pillarStatus, setPillarStatus] = useState("idle");
  const coverRef = useRef(null);
  const pollRef = useRef(null);

  const load = () => Promise.all([api.get("/admin/blog"), api.get("/admin/blog-settings"), api.get("/admin/blog/pillar-status")])
    .then(([p, s, ps]) => {
      setPosts(p.data);
      setSettings({ auto: true, cadence_days: 7, auto_publish: true, ...s.data });
      setPillarStatus(ps.data.status);
      if (ps.data.status === "running" && !pollRef.current) pollPillar();
    })
    .catch(() => {});
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []); // eslint-disable-line

  const pollPillar = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/admin/blog/pillar-status");
        setPillarStatus(r.data.status);
        if (r.data.status !== "running") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (r.data.status === "done") toast.success("Guida completa generata!");
          else if (r.data.status === "error") toast.error("Generazione guida non riuscita: " + (r.data.error || ""));
          load();
        }
      } catch { /* keep polling */ }
    }, 5000);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/admin/blog/generate", { topic: topic.trim() || null, publish });
      toast.success("Articolo generato con l'IA");
      setTopic(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const generatePillar = async () => {
    if (!window.confirm("Genero la 'Guida completa alla Val di Sole' + 9 articoli tematici collegati tra loro? Richiede 1-2 minuti.")) return;
    try {
      await api.post("/admin/blog/generate-pillar");
      setPillarStatus("running");
      toast.info("Generazione avviata. Gli articoli compariranno tra 1-2 minuti.");
      pollPillar();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePublish = async (p) => {
    try { await api.put(`/admin/blog/${p.id}`, { published: !p.published }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    await api.delete(`/admin/blog/${id}`); toast.success("Eliminato"); load();
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/blog-settings", { auto: settings.auto, cadence_days: Number(settings.cadence_days) || 7, auto_publish: settings.auto_publish });
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((ed) => ({ ...ed, cover_image: r.data.url }));
      toast.success("Copertina caricata");
    } catch { toast.error("Upload non riuscito"); }
  };

  const saveEdit = async () => {
    if (!editing.title?.trim()) return toast.error("Inserisci un titolo");
    const payload = {
      title: editing.title,
      meta_description: editing.meta_description || "",
      excerpt: editing.excerpt || "",
      content_html: editing.content_html || "",
      cover_image: editing.cover_image || "",
      is_pillar: !!editing.is_pillar,
      keywords: typeof editing.keywords === "string" ? editing.keywords.split(",").map((k) => k.trim()).filter(Boolean) : (editing.keywords || []),
    };
    try {
      if (editing.isNew) { await api.post("/admin/blog/manual", { ...payload, published: !!editing.published }); toast.success("Articolo creato"); }
      else { await api.put(`/admin/blog/${editing.id}`, payload); toast.success("Articolo aggiornato"); }
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl text-fir flex items-center gap-2"><Newspaper className="text-gold" /> Blog & Guide SEO</h1>
          <p className="text-fir-deep/60">Guide locali per attrarre traffico organico da Google e dai motori AI. Pubblicate su <span className="font-mono">/guide</span>.</p>
        </div>
        <button data-testid="blog-new-manual-btn" onClick={() => setEditing({ ...NEW_DRAFT })} className="gold-btn px-5 py-3 rounded-xl flex items-center gap-2"><PenLine size={18} /> Scrivi articolo</button>
      </div>

      {/* Pillar */}
      <div className="bg-gradient-to-br from-fir to-fir-deep text-ivory rounded-2xl p-6 mb-6" data-testid="blog-pillar-card">
        <h2 className="font-display text-2xl text-gold mb-2 flex items-center gap-2"><Layers size={20} /> Guida "pillar" alla Val di Sole</h2>
        <p className="text-ivory/75 text-sm max-w-3xl">Crea una guida completa collegata a 9 articoli tematici (Estate, Inverno, Sci, Bike, Escursioni, Mercatini, Terme, Ristoranti, Eventi). Questa architettura pillar↔cluster segnala a Google che il sito è autorevole sulla Val di Sole.</p>
        <button data-testid="blog-generate-pillar-btn" onClick={generatePillar} disabled={pillarStatus === "running"}
          className="mt-4 bg-gold text-fir-deep font-semibold px-5 py-3 rounded-xl flex items-center gap-2 disabled:opacity-60">
          {pillarStatus === "running" ? <><Loader2 className="animate-spin" size={18} /> Generazione in corso… (1-2 min)</> : <><Sparkles size={18} /> Genera guida completa</>}
        </button>
      </div>

      {/* Generate single */}
      <div className="bg-white rounded-2xl border border-stone p-6 mb-6" data-testid="blog-generate-form">
        <h2 className="font-display text-2xl text-fir mb-4 flex items-center gap-2"><Sparkles className="text-gold" size={20} /> Genera un singolo articolo</h2>
        <label className="text-xs uppercase tracking-wide text-fir-deep/60">Argomento (opzionale — se vuoto sceglie un tema stagionale)</label>
        <input data-testid="blog-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Es. Le migliori piste per famiglie a Folgarida" className="input-alpine w-full mt-1" />
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <label className="flex items-center gap-2 cursor-pointer text-fir">
            <input type="checkbox" data-testid="blog-publish-toggle" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="accent-gold w-4 h-4" /> Pubblica subito
          </label>
          <button data-testid="blog-generate-btn" onClick={generate} disabled={generating} className="border border-fir text-fir hover:bg-fir hover:text-ivory px-5 py-3 rounded-xl flex items-center gap-2 transition-colors">
            {generating ? <><Loader2 className="animate-spin" size={18} /> Generazione…</> : <><Sparkles size={18} /> Genera con IA</>}
          </button>
        </div>
      </div>

      {/* Auto schedule */}
      <div className="bg-white rounded-2xl border border-stone p-6 mb-8" data-testid="blog-settings-form">
        <h2 className="font-display text-2xl text-fir mb-4 flex items-center gap-2"><Settings2 className="text-gold" size={20} /> Pubblicazione automatica</h2>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <label className="flex items-center gap-2 cursor-pointer text-fir">
            <input type="checkbox" data-testid="blog-auto-toggle" checked={settings.auto} onChange={(e) => setSettings({ ...settings, auto: e.target.checked })} className="accent-gold w-4 h-4" /> Genera automaticamente
          </label>
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Ogni (giorni)</label>
            <input data-testid="blog-cadence-input" type="number" min="1" value={settings.cadence_days} onChange={(e) => setSettings({ ...settings, cadence_days: e.target.value })} className="input-alpine w-full mt-1" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-fir">
            <input type="checkbox" data-testid="blog-autopublish-toggle" checked={settings.auto_publish} onChange={(e) => setSettings({ ...settings, auto_publish: e.target.checked })} className="accent-gold w-4 h-4" /> Pubblica automaticamente
          </label>
        </div>
        {settings.last_run && <p className="text-xs text-fir-deep/50 mt-3">Ultima generazione automatica: {new Date(settings.last_run).toLocaleString("it-IT")}</p>}
        <button data-testid="blog-settings-save-btn" onClick={saveSettings} disabled={savingSettings} className="mt-4 border border-fir text-fir hover:bg-fir hover:text-ivory px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors">
          {savingSettings ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Salva impostazioni
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-stone overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone/40 text-fir-deep/70">
            <tr><th className="text-left p-3">Articolo</th><th className="text-left p-3">Creato</th><th className="text-left p-3">Stato</th><th className="text-right p-3">Azioni</th></tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} data-testid="blog-post-row" className="border-t border-stone">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {p.is_pillar && <span title="Guida principale" className="text-gold"><Crown size={15} /></span>}
                    <span className="font-medium text-fir">{p.title}</span>
                    {p.pillar_slug && <span className="text-[10px] uppercase tracking-wide bg-stone text-fir-deep/60 px-1.5 py-0.5 rounded">cluster</span>}
                  </div>
                  <div className="text-xs text-fir-deep/50 font-mono">/guide/{p.slug}{p.cover_image ? " · 📷" : ""}</div>
                </td>
                <td className="p-3 text-fir-deep/70">{p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT") : "—"}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.published ? "bg-green-100 text-green-700" : "bg-stone text-fir-deep/60"}`}>{p.published ? "Pubblicato" : "Bozza"}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  {p.published && <a href={`/guide/${p.slug}`} target="_blank" rel="noreferrer" className="text-fir hover:text-gold inline-flex items-center mr-3" title="Apri"><ExternalLink size={15} /></a>}
                  <button data-testid="blog-edit-btn" onClick={() => setEditing({ ...p, keywords: (p.keywords || []).join(", ") })} className="text-fir hover:text-gold inline-flex items-center mr-3" title="Modifica"><PenLine size={15} /></button>
                  <button data-testid="blog-toggle-btn" onClick={() => togglePublish(p)} className="text-fir hover:text-gold inline-flex items-center mr-3" title={p.published ? "Nascondi" : "Pubblica"}>{p.published ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                  <button data-testid="blog-delete-btn" onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700 inline-flex items-center" title="Elimina"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-fir-deep/50">Nessun articolo. Scrivine uno o genera la guida completa.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="blog-edit-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl text-fir">{editing.isNew ? "Nuovo articolo" : "Modifica articolo"}</h3>
              <button onClick={() => setEditing(null)} className="text-fir-deep/60 hover:text-fir"><X size={22} /></button>
            </div>
            <div className="space-y-4">
              {/* Cover */}
              <div>
                <label className="text-xs uppercase tracking-wide text-fir-deep/60">Foto di copertina</label>
                <div className="mt-1 flex items-center gap-3">
                  {editing.cover_image ? (
                    <div className="relative">
                      <img src={resolveImg(editing.cover_image)} alt="Copertina" className="h-24 w-40 object-cover rounded-xl border border-stone" />
                      <button onClick={() => setEditing({ ...editing, cover_image: "" })} className="absolute -top-2 -right-2 bg-white border border-stone rounded-full p-1 text-red-500 hover:text-red-700"><X size={13} /></button>
                    </div>
                  ) : (
                    <div className="h-24 w-40 rounded-xl border border-dashed border-stone flex items-center justify-center text-fir-deep/40"><ImageIcon size={22} /></div>
                  )}
                  <div>
                    <button data-testid="blog-cover-upload-btn" onClick={() => coverRef.current?.click()} className="border border-fir text-fir hover:bg-fir hover:text-ivory px-4 py-2 rounded-xl text-sm transition-colors">Carica foto</button>
                    <input ref={coverRef} type="file" accept="image/*" hidden onChange={(e) => { uploadCover(e.target.files?.[0]); e.target.value = ""; }} data-testid="blog-cover-input" />
                    <p className="text-xs text-fir-deep/50 mt-1">JPG/PNG/WEBP, max 15MB</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-fir-deep/60">Titolo</label>
                <input data-testid="blog-edit-title" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="input-alpine w-full mt-1" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-fir-deep/60">Meta description (SEO)</label>
                  <input value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} className="input-alpine w-full mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-fir-deep/60">Keywords (virgola)</label>
                  <input value={editing.keywords || ""} onChange={(e) => setEditing({ ...editing, keywords: e.target.value })} className="input-alpine w-full mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-fir-deep/60">Anteprima (excerpt)</label>
                <input value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="input-alpine w-full mt-1" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-fir-deep/60">Contenuto</label>
                <div className="mt-1">
                  <RichTextEditor value={editing.content_html} onChange={(html) => setEditing((ed) => ({ ...ed, content_html: html }))} />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-fir">
                <input type="checkbox" data-testid="blog-edit-pillar" checked={!!editing.is_pillar} onChange={(e) => setEditing({ ...editing, is_pillar: e.target.checked })} className="accent-gold w-4 h-4" />
                <span className="flex items-center gap-1"><Crown size={14} className="text-gold" /> È la guida principale (pillar)</span>
              </label>
              {editing.isNew && (
                <label className="flex items-center gap-2 cursor-pointer text-fir">
                  <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-gold w-4 h-4" /> Pubblica subito
                </label>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setEditing(null)} className="px-4 py-2.5 rounded-xl border border-stone text-fir-deep/70">Annulla</button>
              <button data-testid="blog-edit-save-btn" onClick={saveEdit} className="gold-btn px-5 py-2.5 rounded-xl flex items-center gap-2"><Save size={16} /> Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
